/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {useState, useCallback} from 'react';
import {useAccessToken} from '@salesforce/commerce-sdk-react';

/**
 * Map Google's administrative_area_level_1 names to form state codes
 * Google often returns full region names that don't match our dropdown values
 */
const GOOGLE_TO_STATE_CODE_MAP = {
  // New Zealand regions - Google returns "X Region" format
  NZ: {
    'Auckland': 'AUK',
    'Auckland Region': 'AUK',
    'Bay of Plenty': 'BOP',
    'Bay of Plenty Region': 'BOP',
    'Canterbury': 'CAN',
    'Canterbury Region': 'CAN',
    'Gisborne': 'GIS',
    'Gisborne Region': 'GIS',
    "Hawke's Bay": 'HKB',
    "Hawke's Bay Region": 'HKB',
    'Marlborough': 'MBH',
    'Marlborough Region': 'MBH',
    'Manawatu-Whanganui': 'MWT',
    'Manawatū-Whanganui': 'MWT',
    'Manawatu-Whanganui Region': 'MWT',
    'Nelson': 'NSN',
    'Nelson Region': 'NSN',
    'Northland': 'NTL',
    'Northland Region': 'NTL',
    'Otago': 'OTA',
    'Otago Region': 'OTA',
    'Southland': 'STL',
    'Southland Region': 'STL',
    'Tasman': 'TAS',
    'Tasman Region': 'TAS',
    'Taranaki': 'TKI',
    'Taranaki Region': 'TKI',
    'Wellington': 'WGN',
    'Wellington Region': 'WGN',
    'Waikato': 'WKO',
    'Waikato Region': 'WKO',
    'West Coast': 'WTC',
    'West Coast Region': 'WTC',
  },
  // Australia - Google usually returns short codes correctly, but add mappings just in case
  AU: {
    'Australian Capital Territory': 'ACT',
    'New South Wales': 'NSW',
    'Northern Territory': 'NT',
    'Queensland': 'QLD',
    'South Australia': 'SA',
    'Tasmania': 'TAS',
    'Victoria': 'VIC',
    'Western Australia': 'WA',
  },
  // Malaysia
  MY: {
    'Johor': 'JHR',
    'Kedah': 'KDH',
    'Kelantan': 'KTN',
    'Melaka': 'MLK',
    'Negeri Sembilan': 'NSN',
    'Pahang': 'PHG',
    'Perak': 'PRK',
    'Perlis': 'PLS',
    'Pulau Pinang': 'PNG',
    'Penang': 'PNG',
    'Sabah': 'SBH',
    'Sarawak': 'SWK',
    'Selangor': 'SGR',
    'Terengganu': 'TRG',
    'Kuala Lumpur': 'KUL',
    'Wilayah Persekutuan Kuala Lumpur': 'KUL',
    'Labuan': 'LBN',
    'Putrajaya': 'PJY',
  },
  // Great Britain counties configured in the Salesforce GB picklist (KL-1088).
  // Google returns the county in administrative_area_level_2 (e.g. "Kent").
  GB: {
    'Buckinghamshire': 'BKM',
    'Cambridgeshire': 'CAM',
    'Cumbria': 'CMA',
    'Derbyshire': 'DBY',
    'Devon': 'DEV',
    'Dorset': 'DOR',
    'Essex': 'ESS',
    'East Sussex': 'ESX',
    'Gloucestershire': 'GLS',
    'Hampshire': 'HAM',
    'Hertfordshire': 'HRT',
    'Kent': 'KEN',
    'Lancashire': 'LAN',
    'Leicestershire': 'LEC',
    'Warwickshire': 'WAR',
  },
};

// UK addresses may resolve to either country code; reuse the GB county map.
GOOGLE_TO_STATE_CODE_MAP.UK = GOOGLE_TO_STATE_CODE_MAP.GB;

/**
 * Convert Google's state/region name to the expected form state code
 * @param {string} googleStateName - State name from Google API
 * @param {string} countryCode - Country code (e.g., 'NZ', 'AU')
 * @returns {string} Mapped state code or original value if no mapping found
 */
const mapGoogleStateToCode = (googleStateName, countryCode) => {
  const countryMap = GOOGLE_TO_STATE_CODE_MAP[countryCode?.toUpperCase()];

  if (countryMap && countryMap[googleStateName]) {
    return countryMap[googleStateName];
  }

  // Return original value if no mapping found
  return googleStateName;
};

/**
 * Custom hook for address autocomplete using Google Places API
 *
 * This hook provides methods to fetch address suggestions from the Google Places API
 * via the /server/autocomplete endpoint. It handles loading states, errors, and
 * provides a clean interface for components to use.
 *
 * Note: Requests are authenticated with SLAS JWT tokens for security (GG-009)
 *
 * @returns {Object} Autocomplete methods and state
 * @property {boolean} loading - Whether a request is in progress
 * @property {string|null} error - Error message if request failed
 * @property {Array} predictions - Array of address predictions from Google Places API
 * @property {Function} getSuggestions - Function to fetch suggestions
 * @property {Function} clearSuggestions - Function to clear current suggestions
 * @property {Function} getPlaceDetails - Function to get parsed address from place_id
 *
 * @example
 * const {loading, error, predictions, getSuggestions, clearSuggestions, getPlaceDetails} = useAddressAutocomplete();
 *
 * // Get suggestions with country filter
 * await getSuggestions('Sydney', {components: 'country:au'});
 *
 * // Get place details for auto-fill
 * const address = await getPlaceDetails(prediction.place_id);
 *
 * // Clear suggestions
 * clearSuggestions();
 */
export const useAddressAutocomplete = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const {getTokenWhenReady} = useAccessToken();

  /**
   * Get address suggestions from the autocomplete endpoint
   *
   * @param {string} input - Partial address text (minimum 2 characters)
   * @param {Object} options - Optional parameters
   * @param {string} options.types - Types of predictions (e.g., 'address', 'geocode')
   * @param {string} options.components - Country restriction (e.g., 'country:au')
   * @param {string} options.language - Language code (e.g., 'en')
   * @param {string} options.sessiontoken - Session token for billing optimization
   * @param {string} options.location - Lat,lng for location bias
   * @param {number} options.radius - Radius in meters for location bias
   * @returns {Promise<Array>} Array of predictions
   */
  const getSuggestions = useCallback(async (input, options = {}) => {
    if (!input || input.trim().length < 2) {
      setPredictions([]);

      return [];
    }

    setLoading(true);
    setError(null);

    try {
      // Get SLAS access token for authentication (GG-009)
      const accessToken = await getTokenWhenReady();

      const params = new URLSearchParams({
        input: input.trim(),
        ...options,
      });

      const response = await fetch(`/server/autocomplete?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch suggestions');
      }

      console.log('[useAddressAutocomplete] 📬 API Response:', {
        status: data.status,
        predictionsCount: data.predictions?.length || 0,
        predictions: data.predictions,
      });

      setPredictions(data.predictions || []);

      return data.predictions || [];
    } catch (err) {
      console.error('[useAddressAutocomplete] Error:', err);
      setError(err.message);
      setPredictions([]);

      return [];
    } finally {
      setLoading(false);
    }
  }, [getTokenWhenReady]);

  /**
   * Clear current predictions and error state
   */
  const clearSuggestions = useCallback(() => {
    setPredictions([]);
    setError(null);
  }, []);

  /**
   * Get place details using place_id to retrieve structured address components
   *
   * @param {string} placeId - Google Places place_id from autocomplete prediction
   * @returns {Promise<Object>} Parsed address components
   */
  const getPlaceDetails = useCallback(async (placeId) => {
    if (!placeId) {
      return null;
    }

    try {
      const accessToken = await getTokenWhenReady();

      const params = new URLSearchParams({
        place_id: placeId,
      });

      const response = await fetch(`/server/geocode?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();

      console.log('[useAddressAutocomplete] 📍 Place Details Response:', data);

      if (!response.ok || data.status !== 'OK' || !data.results?.length) {
        console.warn('[useAddressAutocomplete] No place details found');

        return null;
      }

      // Parse address components from the first result
      const result = data.results[0];
      const components = result.address_components || [];

      // Extract specific address parts
      const parsed = {
        streetNumber: '',
        route: '',
        address1: '',
        city: '',
        stateCode: '',
        postalCode: '',
        countryCode: '',
      };

      // First pass: extract country code (needed for state mapping)
      components.forEach((component) => {
        const types = component.types || [];

        if (types.includes('country')) {
          parsed.countryCode = component.short_name;
        }
      });

      // Second pass: extract all other components. The administrative areas are
      // collected here and state/county is resolved AFTER the loop, because UK/GB
      // counties come from administrative_area_level_2 (e.g. "Warwickshire"), not
      // level_1 — level_1 is the constituent country (England/Scotland/Wales/NI). KL-1088.
      let adminArea1ShortName = '';
      let adminArea1LongName = '';
      let countyName = '';
      let postalTown = '';

      components.forEach((component) => {
        const types = component.types || [];

        if (types.includes('street_number')) {
          parsed.streetNumber = component.long_name;
        } else if (types.includes('route')) {
          parsed.route = component.long_name;
        } else if (types.includes('locality') || types.includes('sublocality_level_1')) {
          parsed.city = component.long_name;
        } else if (types.includes('postal_town')) {
          // UK/IE towns are returned as postal_town (used as a city fallback below)
          postalTown = component.long_name;
        } else if (types.includes('administrative_area_level_1')) {
          adminArea1ShortName = component.short_name;
          adminArea1LongName = component.long_name;
        } else if (types.includes('administrative_area_level_2')) {
          countyName = component.long_name;
        } else if (types.includes('postal_code')) {
          parsed.postalCode = component.long_name;
        }
      });

      // City fallback: UK/IE addresses often have no `locality`, only `postal_town`.
      if (!parsed.city && postalTown) {
        parsed.city = postalTown;
      }

      // State / County resolution
      const isUKAddress = ['GB', 'UK'].includes((parsed.countryCode || '').toUpperCase());

      if (isUKAddress) {
        // County is a dropdown backed by the Salesforce GB picklist. Map Google's
        // county name (level_2, e.g. "Kent") to its picklist code (e.g. "KEN").
        // Fall back to the level_1 name, then leave blank when the county isn't in
        // the configured list — never send an unconfigured value (KL-1088).
        const countyMapped = mapGoogleStateToCode(countyName, parsed.countryCode);
        const areaMapped = mapGoogleStateToCode(adminArea1LongName, parsed.countryCode);

        if (countyName && countyMapped !== countyName) {
          parsed.stateCode = countyMapped;
        } else if (adminArea1LongName && areaMapped !== adminArea1LongName) {
          parsed.stateCode = areaMapped;
        } else {
          parsed.stateCode = '';
        }
      } else if (adminArea1ShortName || adminArea1LongName) {
        // Map Google's state name to the form state code (short_name first, then
        // long_name; otherwise fall back to the raw short_name).
        const shortNameMapped = mapGoogleStateToCode(adminArea1ShortName, parsed.countryCode);
        const longNameMapped = mapGoogleStateToCode(adminArea1LongName, parsed.countryCode);

        if (shortNameMapped !== adminArea1ShortName) {
          parsed.stateCode = shortNameMapped;
        } else if (longNameMapped !== adminArea1LongName) {
          parsed.stateCode = longNameMapped;
        } else {
          parsed.stateCode = adminArea1ShortName;
        }
      }

      // Combine street number and route for address1
      parsed.address1 = [parsed.streetNumber, parsed.route].filter(Boolean).join(' ');

      console.log('[useAddressAutocomplete] ✅ Parsed address:', parsed, '(country:', parsed.countryCode, ')');

      return parsed;
    } catch (err) {
      console.error('[useAddressAutocomplete] Error fetching place details:', err);

      return null;
    }
  }, [getTokenWhenReady]);

  return {
    loading,
    error,
    predictions,
    getSuggestions,
    clearSuggestions,
    getPlaceDetails,
  };
};
